import { goalMetricLabels } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useNow } from "store/selectors/hooks";
import {
    DisplayMetric,
    displayMetricBarColor,
    displayMetrics,
    displayMetricValue,
} from "./display-metrics";
import { MetricCard } from "./metric-card";
import { currentYearMonth, monthElapsedFraction, ymAddMonths, ymLabel } from "./pace";
import { RecentActivityList } from "./recent-activity-list";

//making separate objects here for display metric labels, etc. because "Other Touchpoints" is calls, texts, and emails.  this leaves flexibility to re-use the underlying call/text/email values if needed.
const displayMetricLabels: Record<DisplayMetric, string> = {
    visit: goalMetricLabels.visit,
    otherTouchpoints: "Other Touchpoints",
    referrals: goalMetricLabels.referrals,
    careStarts: goalMetricLabels.careStarts,
};

export const MyGoalsContent: React.FC = () => {
    const now = useNow();
    const trpcClient = trpc.useTRPC();
    const month = currentYearMonth(now);
    const priorMonth = ymAddMonths(month, -1);

    const currentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month }),
    });
    const priorQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month: priorMonth }),
    });
    const recentQuery = useQuery({
        ...trpcClient.marketingGoal.myRecentActivity.queryOptions({ month }),
    });

    const attainment = currentQuery.data;
    const prior = priorQuery.data;
    const frac = monthElapsedFraction(month, now);
    const goal = attainment?.goal ?? null;
    const trackedMetrics: DisplayMetric[] = goal
        ? displayMetrics.filter(metric => displayMetricValue(goal, metric) > 0)
        : [];

    return (
        <div className="space-y-4">
            <div className="flex items-baseline justify-between gap-2">
                <h1 className="text-2xl font-semibold text-zinc-950">{ymLabel(month)}</h1>
                <span className="text-sm text-gray-500">
                    {Math.round(frac * 100)}% through the month
                </span>
            </div>
            {currentQuery.isLoading ? (
                <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-200">
                    <Spinner />
                </div>
            ) : currentQuery.isError ? (
                <div className="rounded-xl bg-white p-8 text-center text-sm text-red-600 ring-1 ring-gray-200">
                    We couldn&apos;t load your goals. Please try again.
                </div>
            ) : !goal || trackedMetrics.length === 0 ? (
                <div className="rounded-xl bg-white p-8 text-center text-sm text-gray-600 ring-1 ring-gray-200">
                    No goals set yet.
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {trackedMetrics.map(metric => (
                            <MetricCard
                                key={metric}
                                label={displayMetricLabels[metric]}
                                barColor={displayMetricBarColor[metric]}
                                actual={displayMetricValue(attainment.actuals, metric)}
                                target={displayMetricValue(goal, metric)}
                                prior={
                                    prior ? displayMetricValue(prior.actuals, metric) : 0
                                }
                                frac={frac}
                            />
                        ))}
                    </div>
                    <p className="text-xs leading-relaxed text-gray-500">
                        Your visits, calls, texts and emails update automatically as you
                        log them. Referrals and care starts update as new clients come in
                        from your referral sources.
                    </p>
                    <RecentActivityList recent={recentQuery.data ?? []} />
                </>
            )}
        </div>
    );
};
